import styled, { css } from 'styled-components';

import { small, bodySemibold, captionRegular } from 'styles/mixins/fonts';

export const Section = styled.div`
  gap: 16px;
  display: flex;
  flex-direction: column;
`;

export const Toolbar = styled.div`
  gap: 8px 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

export const Hint = styled.p`
  ${captionRegular};
  color: ${({ theme }) => theme.colors.grey.primary.standard};
`;

export const PastePanel = styled.div`
  gap: 16px;
  display: flex;
  flex-direction: column;
  max-width: ${({ theme }) => (theme.responsive.isMobile ? 'none' : '640px')};
`;

// Same 8 px bleed as a yellow row: its text starts on the dates' edge.
// An 8 px bleed at every width: its text starts on the form's content line
// (the dates' edge) and the box edge sits 8 px outside it (owner/instructor
// 22 Oct: content alignment wins). Yellow only when a row needs checking;
// green when everything filled.
export const Summary = styled.div<{ $warning: boolean }>`
  ${captionRegular};
  gap: 8px 16px;
  padding: 8px;
  display: flex;
  margin: 0 -8px;
  border-radius: 8px;
  overflow-wrap: anywhere;
  align-items: ${({ theme }) =>
    theme.responsive.isMobile ? 'flex-start' : 'center'};
  flex-direction: ${({ theme }) =>
    theme.responsive.isMobile ? 'column' : 'row'};
  max-width: ${({ theme }) => (theme.responsive.isMobile ? 'none' : '656px')};
  background: ${({ theme, $warning }) =>
    $warning
      ? theme.colors.yellow.tertiary.standard
      : theme.colors.green.tertiary.standard};
  box-shadow: inset 0 0 0 1px
    ${({ theme, $warning }) =>
      $warning
        ? theme.colors.yellow.primary.standard
        : theme.colors.green.primary.standard};
`;

export const SummaryText = styled.div`
  gap: 4px;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const Rows = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

// Every row carries the same padding (and pulls it back with a negative
// margin), so a yellow row never moves its fields: the date and the boxes
// stay on the column edges of the rest of the form.
// On desktop and iPad a row is as wide as what it holds (a yellow row ends
// after its note, never at the screen edge); the 8 px bleed stays inside the
// page's 16 px gutter.
// Every width: the row's content stays on the form's content line; a
// highlighted row's box bleeds 8 px outside it.
export const Row = styled.div<{ $flagged: boolean }>`
  gap: 8px 16px;
  padding: 8px;
  display: grid;
  margin: 0 -8px;
  align-items: end;
  border-radius: 8px;
  ${({ theme }) =>
    theme.responsive.isMobile
      ? css`
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          grid-template-areas:
            'date closed'
            'from to'
            'note note';
        `
      : css`
          width: fit-content;
          max-width: calc(100% + 16px);
          grid-template-columns: 104px 152px 152px max-content minmax(
              0,
              max-content
            );
          grid-template-areas: 'date from to closed note';
        `}
  ${({ theme, $flagged }) =>
    $flagged &&
    css`
      background: ${theme.colors.yellow.tertiary.standard};
      box-shadow: inset 0 0 0 1px ${theme.colors.yellow.primary.standard};
    `}
`;

export const DateLabel = styled.span`
  ${bodySemibold};
  grid-area: date;
  display: flex;
  white-space: nowrap;
  align-items: center;
  height: ${({ theme }) => (theme.responsive.isMobile ? '24px' : '40px')};
`;

export const FromCell = styled.div`
  min-width: 0;
  grid-area: from;
`;

export const ToCell = styled.div`
  min-width: 0;
  grid-area: to;
`;

export const ClosedCell = styled.div`
  display: flex;
  grid-area: closed;
  white-space: nowrap;
  align-items: center;
  justify-self: ${({ theme }) => (theme.responsive.isMobile ? 'end' : 'start')};
  height: ${({ theme }) => (theme.responsive.isMobile ? '24px' : '40px')};
`;

// The row's note: the form's normal 14 px text (4th pass: the 12 px caption
// was too small at 1440). Reason, "Keep these hours" and "Edited · Use
// text" sit on one line, centred on the time boxes (under them on a phone).
export const RowNote = styled.span<{ $error: boolean }>`
  ${small};
  gap: 4px 12px;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  grid-area: note;
  align-items: center;
  overflow-wrap: anywhere;
  min-height: ${({ theme }) => (theme.responsive.isMobile ? 'auto' : '40px')};
  color: ${({ theme, $error }) =>
    $error ? theme.colors.red.primary.standard : 'inherit'};
`;

// "Edited · Use text": a row the box no longer overwrites.
export const EditedTag = styled.span`
  ${small};
  white-space: nowrap;
`;

export const TextAction = styled.button`
  ${small};
  white-space: nowrap;
  padding: 0;
  border: none;
  cursor: pointer;
  background: none;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.blue.primary.standard};
`;

// Save blocked by a yellow row (the form's error colour).
export const BlockMessage = styled.p`
  ${small};
  font-weight: 600;
  color: ${({ theme }) => theme.config.error.color};
`;

// The line under the hours box: the form's helper text, 8 px under the box.
export const BoxHelp = styled(Hint)`
  margin-top: -8px;
`;
